// src/pages/admin/editorTabs/GeneralTab.jsx
import React, { useRef, useState } from "react";
import { slugify } from "../../../data/pagesStore";

export default function GeneralTab({
  pageId,
  pageName,
  setPageName,
  pageSlug,
  setPageSlug,
  general,
  setGeneral,
  uploadImage,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const previewSlug = slugify(pageSlug || pageName);

  const pickLogo = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      setUploading(true);
      const url = await uploadImage(file, { pageId, kind: "logo" });
      setGeneral((prev) => ({ ...(prev || {}), logoUrl: url }));
    } catch (err) {
      alert(err?.message || "Logo upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-4">
      <div className="font-bold text-gray-900">General</div>

      <div>
        <div className="text-sm font-medium text-gray-700 mb-1">Business name</div>
        <input
          value={pageName}
          onChange={(e) => setPageName(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg"
          placeholder="Business name"
        />
      </div>

      <div>
        <div className="text-sm font-medium text-gray-700 mb-1">Slug (URL)</div>
        <input
          value={pageSlug}
          onChange={(e) => setPageSlug(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg"
          placeholder="my-business"
        />
        <div className="text-xs text-gray-500 mt-1">URL: /{previewSlug}</div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">Phone</div>
          <input
            value={general?.phone || ""}
            onChange={(e) => setGeneral((prev) => ({ ...(prev || {}), phone: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="9725..."
          />
        </div>

        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">City</div>
          <input
            value={general?.city || ""}
            onChange={(e) => setGeneral((prev) => ({ ...(prev || {}), city: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Netanya"
          />
        </div>

        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">Page Background Color</div>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={general?.pageBgColor || "#ffffff"}
              onChange={(e) =>
                setGeneral((prev) => ({ ...(prev || {}), pageBgColor: e.target.value }))
              }
              className="h-10 w-16 p-1 border rounded-lg bg-white cursor-pointer"
            />
            <input
              type="text"
              value={general?.pageBgColor || "#ffffff"}
              onChange={(e) =>
                setGeneral((prev) => ({ ...(prev || {}), pageBgColor: e.target.value }))
              }
              className="flex-1 px-3 py-2 border rounded-lg"
              placeholder="#ffffff"
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            This sets the main background color of the whole page.
          </div>
        </div>

        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">
            Page Language
          </div>

          <select
            value={general?.language || "en"}
            onChange={(e) => {
              const lang = e.target.value;

              setGeneral((prev) => ({
                ...(prev || {}),
                language: lang,
                direction: lang === "ar" || lang === "he" ? "rtl" : "ltr",
              }));
            }}
            className="w-full px-3 py-2 border rounded-lg bg-white"
          >
            <option value="en">English</option>
            <option value="ar">Arabic</option>
            <option value="he">Hebrew</option>
          </select>

          <div className="text-xs text-gray-500 mt-1">
            This controls text direction and layout of the page.
          </div>
        </div>

        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Logo</div>

          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl border bg-gray-50 overflow-hidden flex items-center justify-center">
              {general?.logoUrl ? (
                <img src={general.logoUrl} alt="logo" className="h-full w-full object-cover" />
              ) : (
                <span className="text-xl">🏷️</span>
              )}
            </div>

            <button
              type="button"
              onClick={pickLogo}
              disabled={uploading || !pageId}
              className="px-3 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Upload logo"}
            </button>

            {general?.logoUrl && (
              <button
                type="button"
                onClick={() => setGeneral((prev) => ({ ...(prev || {}), logoUrl: "" }))}
                className="px-3 py-2 rounded-lg border text-red-600 hover:bg-red-50"
              >
                Remove
              </button>
            )}

          </div>
          <div className="mt-6 border-t pt-4">
            <div className="font-bold text-gray-900 mb-3">Scroll Effects</div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Section reveal animation</div>
                <select
                  value={general?.scrollAnim || "fadeUp"}
                  onChange={(e) => setGeneral((prev) => ({ ...(prev || {}), scrollAnim: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="none">None</option>
                  <option value="fadeUp">Fade up</option>
                  <option value="fade">Fade</option>
                  <option value="slideLeft">Slide from left</option>
                  <option value="slideRight">Slide from right</option>
                  <option value="zoom">Zoom</option>
                </select>
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Animate once</div>
                <label className="flex items-center gap-2 px-3 py-2 border rounded-lg">
                  <input
                    type="checkbox"
                    checked={typeof general?.scrollOnce === "boolean" ? general.scrollOnce : true}
                    onChange={(e) => setGeneral((prev) => ({ ...(prev || {}), scrollOnce: e.target.checked }))}
                  />
                  <span className="text-sm text-gray-700">Only first time a section appears</span>
                </label>
              </div>

              <div className="sm:col-span-2">
                <div className="text-sm font-medium text-gray-700 mb-1">Anchor scroll behavior</div>
                <select
                  value={general?.scrollBehavior || "smooth"}
                  onChange={(e) => setGeneral((prev) => ({ ...(prev || {}), scrollBehavior: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="auto">Instant</option>
                  <option value="smooth">Smooth</option>
                </select>
                <div className="text-xs text-gray-500 mt-1">
                  This affects scrolling when clicking navbar or footer links.
                </div>
              </div>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />
          <div className="text-xs text-gray-500 mt-2">
            Tip: this is stored in page.general.logoUrl. You can show it in your navbar or hero template later.
          </div>
        </div>
      </div>
    </div>
  );
}